import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MailrailDripStep, TUnitOfTime } from '@feathr/blackbox';
import { EDripTriggerAction } from '@feathr/blackbox';
import { Fieldset, NumberInput, Select, Well } from '@feathr/components';
import { flattenError } from '@feathr/hooks';

import type { ICampaignValidationErrors } from '../../../CampaignSummary';
import type { ICustomSelectOption } from '../../DripCampaignEdit/BuilderStep/EditAutomation/TargetingOption';
import TargetingOption from '../../DripCampaignEdit/BuilderStep/EditAutomation/TargetingOption';
import useDripText from '../../DripCampaignEdit/useDripText';
import {
  bestUnit,
  getTimeUnitOptions,
  getTimeUnitOptionValue,
  type ITimeUnitOption,
  minimumDisplayValues,
  toDisplayValue,
  toSeconds,
} from '../timeUnits.utils';

export interface IDripStepTargetingProps {
  /** The ephemeral drip step being edited. */
  mailing: MailrailDripStep;
  /** Grouped validation errors for the parent campaign/step. */
  validationErrors: ICampaignValidationErrors;
  /** Whether the inputs are disabled. */
  disabled?: boolean;
}

/**
 * Step targeting selector for a Mailrail drip step. Lets the user choose which
 * trigger action gates the step (over the five {@link EDripTriggerAction}
 * members) and configure a friendly value + unit delay that is persisted to the
 * step's `delay_seconds`.
 */
function DripStepTargeting({
  mailing,
  validationErrors,
  disabled = false,
}: Readonly<IDripStepTargetingProps>): JSX.Element {
  const { t } = useTranslation();
  const {
    builder: { targetingDelayHelpText, targetingDelayLabel, targetingMessage },
  } = useDripText();

  const trigger = mailing.get('trigger_action');
  const delaySeconds = mailing.get('delay_seconds') ?? 0;

  const [unit, setUnit] = useState<TUnitOfTime>(() => bestUnit(delaySeconds));

  const targetingOptions: ICustomSelectOption[] = [
    {
      id: EDripTriggerAction.previous_email_delivered,
      name: targetingMessage[EDripTriggerAction.previous_email_delivered],
      description: t('This email will be sent to everybody that received the previous email.'),
    },
    {
      id: EDripTriggerAction.not_opened_previous_email,
      name: targetingMessage[EDripTriggerAction.not_opened_previous_email],
      description: t(
        'This email will only be sent to people that received but did not open the previous email.',
      ),
    },
    {
      id: EDripTriggerAction.opened_previous_email,
      name: targetingMessage[EDripTriggerAction.opened_previous_email],
      description: t('This email will only be sent to people that have opened the previous email.'),
    },
    {
      id: EDripTriggerAction.not_clicked_link_previous_email,
      name: targetingMessage[EDripTriggerAction.not_clicked_link_previous_email],
      description: t(
        'This email will only be sent to people that received but did not click a link in the previous email.',
      ),
    },
    {
      id: EDripTriggerAction.clicked_link_previous_email,
      name: targetingMessage[EDripTriggerAction.clicked_link_previous_email],
      description: t(
        'This email will only be sent to people that have clicked a link in the previous email.',
      ),
    },
  ];

  const timeOptions = getTimeUnitOptions();

  function handleTargetingSelect(option: ICustomSelectOption): void {
    mailing.set({ trigger_action: option.id });
  }

  function getOptionLabelDelay(option: ITimeUnitOption): string | undefined {
    const count = toDisplayValue(delaySeconds, option.id);
    return {
      minutes: t('minute', { count }),
      hours: t('hour', { count }),
      days: t('day', { count }),
      weeks: t('week', { count }),
    }[option.id];
  }

  function handleChangeDelayValue(newValue?: number): void {
    const displayValue = newValue ?? minimumDisplayValues[unit];
    mailing.set({ delay_seconds: toSeconds(displayValue, unit) });
  }

  function handleSelectUnit(option: ITimeUnitOption): void {
    setUnit(option.id);
    const minimum = minimumDisplayValues[option.id];
    const displayValue = Math.max(toDisplayValue(delaySeconds, option.id), minimum);
    mailing.set({ delay_seconds: toSeconds(displayValue, option.id) });
  }

  const selectedOption = targetingOptions.find((option) => option.id === trigger);

  return (
    <Well layout={'vertical'} theme={'white'}>
      <Fieldset>
        <Select
          additionalContent={!!selectedOption && <p>{selectedOption.description}</p>}
          components={{ Option: TargetingOption }}
          disabled={disabled}
          helpText={t('Select who should be sent the email in this step.')}
          label={t('Step targeting')}
          name={'step-targeting'}
          onSelectSingle={handleTargetingSelect}
          options={targetingOptions}
          placeholder={t('Select one')}
          value={selectedOption}
        />
        {trigger && (
          <NumberInput
            disabled={disabled}
            helpText={targetingDelayHelpText[trigger]}
            label={targetingDelayLabel[trigger]}
            min={minimumDisplayValues[unit]}
            name={'delay_value'}
            onChange={handleChangeDelayValue}
            suffix={
              <Select
                disabled={disabled}
                getOptionLabel={getOptionLabelDelay}
                getOptionValue={getTimeUnitOptionValue}
                name={'delay_unit'}
                onSelectSingle={handleSelectUnit}
                options={timeOptions}
                value={timeOptions.find((option) => option.id === unit)}
              />
            }
            validationError={flattenError(validationErrors.delay_seconds)}
            value={toDisplayValue(delaySeconds, unit)}
          />
        )}
      </Fieldset>
    </Well>
  );
}

export default observer(DripStepTargeting);
